import React, { useState } from 'react';
import axios from 'axios';
import './index.css';
import { POST_CAPITAL_OFFICE_DATA } from '../../api/capitalOffice';
import {
    validateRequired,
    validateEmail,
    validateUrl,
    validateNumeric,
    validatePercentage,
    validateInteger,
    validateDate,
    validateSelect,
    validateBoolean,
} from '../../utils/validators';
import { uploadDocumentsToS3 } from '../../utils/s3Upload';

const steps = [
    { id: 1, label: 'Company', percent: 20 },
    { id: 2, label: 'Round', percent: 40 },
    { id: 3, label: 'Traction', percent: 60 },
    { id: 4, label: 'Investor fit', percent: 80 },
    { id: 5, label: 'Review', percent: 100 },
];

const initialFormData = {
    // Step 1: Company
    founderName: '',
    workEmail: '',
    companyName: '',
    website: '',
    headquarters: '',
    sector: '',
    oneLineDesc: '',
    step1Confirmed: false,

    // Step 2: Round
    roundStage: '',
    targetRaise: '',
    capitalRaised: '',
    targetCloseDate: '',
    valuationApproach: '',
    currentCommitments: '',
    leadStatus: 'Not started',
    useOfFunds: ['Product', 'Sales & growth'],
    roundContext: '',

    // Step 3: Traction
    businessModel: '',
    revenueStatus: 'Revenue generating',
    monthlyRevenue: '',
    revenuePeriod: '',
    revenueGrowth: '',
    grossMargin: '',
    activeCustomers: '',
    monthlyBurn: '',
    runwayRemaining: '',
    teamSize: '',
    tractionSignals: ['Recurring revenue', 'Enterprise contracts'],
    proofPoint: '',

    // Step 4: Investor Fit
    investorTypes: ['Venture fund', 'Micro VC'],
    targetGeography: 'Singapore, UAE, UK',
    minCheque: '',
    maxCheque: '',
    sectorsSought: '',
    strategicValue: ['Industry expertise', 'Customer access'],
    existingConversations: 'None yet',
    investorExclusions: '',
    idealInvestor: '',
    authAssessment: false,

    // Step 5: Review Declarations & Supporting Materials
    documents: [],
    dataRoomLink: '',
    declarationAccuracy: false,
    declarationNoGuarantee: false,
};

const CapitalOfficeApplication = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [submittingStatus, setSubmittingStatus] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [showDataRoomInput, setShowDataRoomInput] = useState(false);
    const [tempDataRoomLink, setTempDataRoomLink] = useState('');

    const validateStep = (step) => {
        const errors = {};

        if (step === 1) {
            const nameErr = validateRequired(formData.founderName, 'Founder name', 2);
            if (nameErr) errors.founderName = nameErr;

            const emailErr = validateEmail(formData.workEmail, 'Work email', true);
            if (emailErr) errors.workEmail = emailErr;

            const companyErr = validateRequired(formData.companyName, 'Company name', 2);
            if (companyErr) errors.companyName = companyErr;

            if (formData.website) {
                const urlErr = validateUrl(formData.website, 'Company website', false);
                if (urlErr) errors.website = urlErr;
            }

            const hqErr = validateRequired(formData.headquarters, 'Headquarters location', 2);
            if (hqErr) errors.headquarters = 'Please enter headquarters location (e.g. City, Country).';

            const sectorErr = validateSelect(formData.sector, 'a sector');
            if (sectorErr) errors.sector = sectorErr;

            const descErr = validateRequired(formData.oneLineDesc, 'One-line company description', 5);
            if (descErr) errors.oneLineDesc = 'Please enter a one-line company description (minimum 5 characters).';

            const confirmErr = validateBoolean(formData.step1Confirmed, 'Please confirm that the information shared is accurate before continuing.');
            if (confirmErr) errors.step1Confirmed = confirmErr;
        } else if (step === 2) {
            const stageErr = validateSelect(formData.roundStage, 'a round stage');
            if (stageErr) errors.roundStage = stageErr;

            const targetRaiseErr = validateNumeric(formData.targetRaise, 'Target raise amount', true);
            if (targetRaiseErr) errors.targetRaise = targetRaiseErr;

            if (formData.capitalRaised) {
                const capErr = validateNumeric(formData.capitalRaised, 'Capital raised to date', false);
                if (capErr) errors.capitalRaised = capErr;
            }

            const dateErr = validateDate(formData.targetCloseDate, 'Target close date', true);
            if (dateErr) errors.targetCloseDate = dateErr;

            if (formData.currentCommitments) {
                const comErr = validateNumeric(formData.currentCommitments, 'Current commitments', false);
                if (comErr) errors.currentCommitments = comErr;
            }

            const contextErr = validateRequired(formData.roundContext, 'Round context', 5);
            if (contextErr) errors.roundContext = 'Please provide round context (minimum 5 characters).';
        } else if (step === 3) {
            const bmErr = validateSelect(formData.businessModel, 'a business model');
            if (bmErr) errors.businessModel = bmErr;

            const revErr = validateNumeric(formData.monthlyRevenue, 'Latest monthly revenue', true);
            if (revErr) errors.monthlyRevenue = revErr;

            const periodErr = validateSelect(formData.revenuePeriod, 'a revenue period');
            if (periodErr) errors.revenuePeriod = periodErr;

            // Realistic revenue growth validation (realistic upper bound e.g. 1000% instead of unrealistic 10000%)
            if (formData.revenueGrowth) {
                const growthErr = validatePercentage(formData.revenueGrowth, '12-month revenue growth', -100, 1000, false);
                if (growthErr) errors.revenueGrowth = growthErr;
            }

            if (formData.grossMargin) {
                const marginErr = validatePercentage(formData.grossMargin, 'Gross margin', 0, 100, false);
                if (marginErr) errors.grossMargin = marginErr;
            }

            if (formData.activeCustomers) {
                const custErr = validateInteger(formData.activeCustomers, 'Active customers', false);
                if (custErr) errors.activeCustomers = custErr;
            }

            if (formData.monthlyBurn) {
                const burnErr = validateNumeric(formData.monthlyBurn, 'Monthly burn', false);
                if (burnErr) errors.monthlyBurn = burnErr;
            }

            const runwayErr = validateSelect(formData.runwayRemaining, 'runway remaining');
            if (runwayErr) errors.runwayRemaining = runwayErr;

            if (formData.teamSize) {
                const teamErr = validateInteger(formData.teamSize, 'Team size', false);
                if (teamErr) errors.teamSize = teamErr;
            }

            const proofErr = validateRequired(formData.proofPoint, 'Strongest proof point', 5);
            if (proofErr) errors.proofPoint = 'Please provide your strongest proof point (minimum 5 characters).';
        } else if (step === 4) {
            const geoErr = validateRequired(formData.targetGeography, 'Target investor geography', 2);
            if (geoErr) errors.targetGeography = 'Please enter target investor geography.';

            if (formData.minCheque) {
                const minErr = validateNumeric(formData.minCheque, 'Minimum cheque size', false);
                if (minErr) errors.minCheque = minErr;
            }

            if (formData.maxCheque) {
                const maxErr = validateNumeric(formData.maxCheque, 'Maximum cheque size', false);
                if (maxErr) errors.maxCheque = maxErr;
            }

            const idealErr = validateRequired(formData.idealInvestor, 'Ideal investor description', 5);
            if (idealErr) errors.idealInvestor = 'Please describe your ideal investor (minimum 5 characters).';

            const authErr = validateBoolean(formData.authAssessment, 'Please authorise Markoknow to assess fit before continuing.');
            if (authErr) errors.authAssessment = authErr;
        } else if (step === 5) {
            if (formData.dataRoomLink) {
                const drErr = validateUrl(formData.dataRoomLink, 'Data room link', false);
                if (drErr) errors.dataRoomLink = drErr;
            }
            const accErr = validateBoolean(formData.declarationAccuracy, 'Please confirm that the information in this application is accurate and current.');
            if (accErr) errors.declarationAccuracy = accErr;

            const noGuarErr = validateBoolean(formData.declarationNoGuarantee, 'Please confirm that submission does not guarantee investor introductions or funding.');
            if (noGuarErr) errors.declarationNoGuarantee = noGuarErr;
        }

        return errors;
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errorMsg) setErrorMsg('');
        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const toggleArrayItem = (field, item) => {
        setFormData((prev) => {
            const exists = prev[field].includes(item);
            return {
                ...prev,
                [field]: exists
                    ? prev[field].filter((i) => i !== item)
                    : [...prev[field], item],
            };
        });
        if (errorMsg) setErrorMsg('');
    };

    // Store selected files locally; they will be uploaded to S3 upon clicking Submit
    const handleFileSelection = (e, docType = 'Supporting Document') => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setErrorMsg('');

        const newSelectedDocs = Array.from(files).map((file) => ({
            name: file.name,
            size: (file.size / 1024).toFixed(1) + ' KB',
            docType: docType,
            rawFile: file,
            isPending: true,
        }));

        setFormData((prev) => ({
            ...prev,
            documents: [...(prev.documents || []), ...newSelectedDocs],
        }));

        if (e.target) e.target.value = '';
    };

    const handleRemoveDoc = (index) => {
        setFormData((prev) => ({
            ...prev,
            documents: prev.documents.filter((_, i) => i !== index),
        }));
    };

    const handleNext = async (e) => {
        if (e) e.preventDefault();

        const errors = validateStep(currentStep);
        if (Object.keys(errors).length > 0) {
            const firstMsg = Object.values(errors)[0];
            setErrorMsg(firstMsg);
            setFieldErrors(errors);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setFieldErrors({});
        setErrorMsg('');

        if (currentStep < 5) {
            setCurrentStep((prev) => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Final step validation & submission with S3 bucket upload
        if (!formData.declarationAccuracy || !formData.declarationNoGuarantee) {
            setErrorMsg('Please confirm all declarations before submitting your application.');
            setFieldErrors({
                ...(!formData.declarationAccuracy ? { declarationAccuracy: 'Accuracy declaration required' } : {}),
                ...(!formData.declarationNoGuarantee ? { declarationNoGuarantee: 'Acknowledgement required' } : {}),
            });
            return;
        }

        setLoading(true);
        try {
            // Step A: Upload any pending documents to AWS S3 bucket upon submit button click
            let finalDocuments = [];
            if (formData.documents && formData.documents.length > 0) {
                setSubmittingStatus('Uploading documents...');
                finalDocuments = await uploadDocumentsToS3(formData.documents, 'capitaloffice');
            }

            // Step B: Submit final application data with S3 document URLs
            setSubmittingStatus('Submitting application...');
            const payload = {
                ...formData,
                documents: finalDocuments,
            };

            const response = await axios.post(POST_CAPITAL_OFFICE_DATA, payload);
            if (response.status === 201 || response.status === 200) {
                setSubmitted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Error submitting Capital Office application:', error);
            if (error.response && error.response.data) {
                const details = error.response.data.details;
                if (Array.isArray(details)) {
                    setErrorMsg(details.join('. '));
                } else if (typeof details === 'string') {
                    setErrorMsg(details);
                } else if (error.response.data.error) {
                    setErrorMsg(error.response.data.error);
                } else {
                    setErrorMsg('An error occurred while submitting your application. Please try again.');
                }
            } else {
                setErrorMsg(error.message || 'Network error. Please check your connection and try again.');
            }
        } finally {
            setLoading(false);
            setSubmittingStatus('');
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
            setErrorMsg('');
            setFieldErrors({});
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setCurrentStep(1);
        setSubmitted(false);
        setErrorMsg('');
        setFieldErrors({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const currentPercent = steps[currentStep - 1].percent;

    return (
        <div className="coa-page">
                {/* 1. Page Sub-Header */}
                <div className="coa-sub-header">
                    <div className="coa-sub-tag">CAPITAL OFFICE APPLICATION</div>
                    <h1 className="coa-sub-title">
                        {currentStep === 1 && 'Prepare your round.'}
                        {currentStep === 2 && 'Define your round.'}
                        {currentStep === 3 && 'Show the evidence.'}
                        {currentStep === 4 && 'Find the right fit.'}
                        {currentStep === 5 && 'Review your round.'}
                    </h1>
                    <p className="coa-sub-desc">
                        {currentStep === 1 && "Tell us where your company stands today. We'll review your round, investor fit and preparation needs before opening a private room."}
                        {currentStep === 2 && "Share the structure, timing and capital target for this raise. Clear parameters help us assess mandate relevance."}
                        {currentStep === 3 && "Share the operating signals behind your story. We use traction to understand momentum, resilience and investor relevance."}
                        {currentStep === 4 && "Define the investors, expertise and relationships that can strengthen this round—not simply fill the allocation."}
                        {currentStep === 5 && "Check the complete application before sending it for a confidential Capital Office assessment."}
                    </p>
                </div>

                {/* 3. Multi-Step Stepper Progress Bar */}
                <div className="coa-stepper-container">
                    <div className="coa-stepper-track">
                        <div
                            className="coa-stepper-progress-fill"
                            style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
                        ></div>

                        {steps.map((step) => {
                            const isCompleted = currentStep > step.id;
                            const isActive = currentStep === step.id;
                            return (
                                <div
                                    key={step.id}
                                    className={`coa-step-node ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                                    onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                                >
                                    <div className="coa-node-circle">
                                        {isCompleted ? '✓' : `0${step.id}`}
                                    </div>
                                    <div className="coa-node-labels">
                                        <span className="coa-node-name">{step.label}</span>
                                        {isActive && <span className="coa-node-pct">{step.percent}% complete</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 4. Form Split View Container */}
                <div className="coa-main-layout">
                    {/* Left Form Panel */}
                    <div className="coa-form-card">
                        {submitted ? (
                            <div className="coa-success-card">
                                <div className="coa-success-icon-badge">✓</div>
                                <h2 className="coa-success-title">Application Submitted</h2>
                                <p className="coa-success-subtitle">
                                    Thank you, <strong>{formData.founderName}</strong>. Your Capital Office application for <strong>{formData.companyName}</strong> has been received for confidential review.
                                </p>
                                <div className="coa-success-summary">
                                    <div className="coa-success-row">
                                        <span className="coa-success-row-label">Work Email</span>
                                        <span className="coa-success-row-value">{formData.workEmail}</span>
                                    </div>
                                    <div className="coa-success-row">
                                        <span className="coa-success-row-label">Round Stage & Target</span>
                                        <span className="coa-success-row-value">{formData.roundStage} · USD {formData.targetRaise}</span>
                                    </div>
                                    <div className="coa-success-row">
                                        <span className="coa-success-row-label">Sector & Location</span>
                                        <span className="coa-success-row-value">{formData.sector} · {formData.headquarters}</span>
                                    </div>
                                    <div className="coa-success-row">
                                        <span className="coa-success-row-label">Target Geography</span>
                                        <span className="coa-success-row-value">{formData.targetGeography}</span>
                                    </div>
                                </div>
                                <p className="coa-success-note">
                                    Our Capital Office mandate curation team will review your parameters against active investor mandates and get back to you within 5–7 business days.
                                </p>
                                <button
                                    type="button"
                                    className="coa-btn-submit-another"
                                    onClick={handleReset}
                                >
                                    Submit Another Application
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleNext}>
                                {errorMsg && (
                                    <div className="coa-alert-error">
                                        <span>⚠️</span> {errorMsg}
                                    </div>
                                )}

                                {/* ---------------- STEP 1: COMPANY ---------------- */}
                                {currentStep === 1 && (
                                    <div className="coa-step-body">
                                        <div className="coa-section-header">
                                            <h2>Company profile</h2>
                                            <p>Start with the essentials</p>
                                        </div>

                                        <div className="coa-grid-2">
                                            <div className="coa-input-group">
                                                <label>Founder name</label>
                                                <input
                                                    type="text"
                                                    placeholder="Your full name"
                                                    value={formData.founderName}
                                                    className={fieldErrors.founderName ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('founderName', e.target.value)}
                                                    required
                                                />
                                                {fieldErrors.founderName && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.founderName}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Work email</label>
                                                <input
                                                    type="email"
                                                    placeholder="you@company.com"
                                                    value={formData.workEmail}
                                                    className={fieldErrors.workEmail ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('workEmail', e.target.value)}
                                                    required
                                                />
                                                {fieldErrors.workEmail && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.workEmail}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Company name</label>
                                                <input
                                                    type="text"
                                                    placeholder="Registered or operating name"
                                                    value={formData.companyName}
                                                    className={fieldErrors.companyName ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('companyName', e.target.value)}
                                                    required
                                                />
                                                {fieldErrors.companyName && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.companyName}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Company website</label>
                                                <input
                                                    type="text"
                                                    placeholder="https://"
                                                    value={formData.website}
                                                    className={fieldErrors.website ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('website', e.target.value)}
                                                />
                                                {fieldErrors.website && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.website}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Headquarters</label>
                                                <input
                                                    type="text"
                                                    placeholder="City, Country"
                                                    value={formData.headquarters}
                                                    className={fieldErrors.headquarters ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('headquarters', e.target.value)}
                                                    required
                                                />
                                                {fieldErrors.headquarters && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.headquarters}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Sector</label>
                                                <select
                                                    value={formData.sector}
                                                    className={fieldErrors.sector ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('sector', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select sector</option>
                                                    <option value="B2B SaaS">B2B SaaS</option>
                                                    <option value="Fintech">Fintech</option>
                                                    <option value="Healthtech">Healthtech</option>
                                                    <option value="Consumer Tech">Consumer Tech</option>
                                                    <option value="Deep Tech">Deep Tech</option>
                                                </select>
                                                {fieldErrors.sector && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.sector}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group full-width">
                                                <label>One-line company description</label>
                                                <textarea
                                                    rows={3}
                                                    placeholder="What do you build, for whom, and why does it matter?"
                                                    value={formData.oneLineDesc}
                                                    className={fieldErrors.oneLineDesc ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('oneLineDesc', e.target.value)}
                                                    required
                                                ></textarea>
                                                {fieldErrors.oneLineDesc && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.oneLineDesc}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="coa-checkbox-wrap">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.step1Confirmed}
                                                    onChange={(e) => handleChange('step1Confirmed', e.target.checked)}
                                                    required
                                                />
                                                <span>I confirm that the information shared is accurate and may be reviewed by the Markoknow Capital Office team.</span>
                                            </label>
                                            {fieldErrors.step1Confirmed && (
                                                <span className="coa-field-error-msg" style={{ marginTop: '8px' }}>⚠️ {fieldErrors.step1Confirmed}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ---------------- STEP 2: ROUND ---------------- */}
                                {currentStep === 2 && (
                                    <div className="coa-step-body">
                                        <div className="coa-section-header">
                                            <h2>Round details</h2>
                                            <p>Set the parameters for your raise</p>
                                        </div>

                                        <div className="coa-grid-2">
                                            <div className="coa-input-group">
                                                <label>Round stage</label>
                                                <select
                                                    value={formData.roundStage}
                                                    className={fieldErrors.roundStage ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('roundStage', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select round stage</option>
                                                    <option value="Pre-seed">Pre-seed</option>
                                                    <option value="Seed">Seed</option>
                                                    <option value="Series A">Series A</option>
                                                    <option value="Series B+">Series B+</option>
                                                </select>
                                                {fieldErrors.roundStage && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.roundStage}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Target raise</label>
                                                <div className={`coa-input-split ${fieldErrors.targetRaise ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter amount (e.g. 2.5M or 2,500,000)"
                                                        value={formData.targetRaise}
                                                        onChange={(e) => handleChange('targetRaise', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                {fieldErrors.targetRaise && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.targetRaise}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Capital raised to date</label>
                                                <div className={`coa-input-split ${fieldErrors.capitalRaised ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter amount (e.g. 500K or 500,000)"
                                                        value={formData.capitalRaised}
                                                        onChange={(e) => handleChange('capitalRaised', e.target.value)}
                                                    />
                                                </div>
                                                {fieldErrors.capitalRaised && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.capitalRaised}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Target close date</label>
                                                <input
                                                    type="text"
                                                    placeholder="MM / YYYY (e.g. 03/2027 or March 2027)"
                                                    value={formData.targetCloseDate}
                                                    className={fieldErrors.targetCloseDate ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('targetCloseDate', e.target.value)}
                                                    required
                                                />
                                                {fieldErrors.targetCloseDate && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.targetCloseDate}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Valuation approach</label>
                                                <select
                                                    value={formData.valuationApproach}
                                                    className={fieldErrors.valuationApproach ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('valuationApproach', e.target.value)}
                                                >
                                                    <option value="">Select preference</option>
                                                    <option value="Priced round">Priced round</option>
                                                    <option value="SAFE / Convertible note">SAFE / Convertible note</option>
                                                    <option value="Market discovered">Market discovered</option>
                                                </select>
                                                {fieldErrors.valuationApproach && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.valuationApproach}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Current commitments</label>
                                                <div className={`coa-input-split ${fieldErrors.currentCommitments ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Amount committed, if any"
                                                        value={formData.currentCommitments}
                                                        onChange={(e) => handleChange('currentCommitments', e.target.value)}
                                                    />
                                                </div>
                                                {fieldErrors.currentCommitments && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.currentCommitments}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Lead investor status</label>
                                                <div className="coa-radio-group">
                                                    {['Not started', 'In conversation', 'Committed'].map((opt) => (
                                                        <label key={opt} className={`coa-radio-pill ${formData.leadStatus === opt ? 'active' : ''}`}>
                                                            <input
                                                                type="radio"
                                                                name="leadStatus"
                                                                value={opt}
                                                                checked={formData.leadStatus === opt}
                                                                onChange={(e) => handleChange('leadStatus', e.target.value)}
                                                            />
                                                            <span>{opt}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Primary use of funds</label>
                                                <div className="coa-pills-selector">
                                                    {['Product', 'Hiring', 'Sales & growth', 'Market expansion', 'Operations', 'Other'].map((item) => (
                                                        <button
                                                            type="button"
                                                            key={item}
                                                            className={`coa-tag-pill ${formData.useOfFunds.includes(item) ? 'selected' : ''}`}
                                                            onClick={() => toggleArrayItem('useOfFunds', item)}
                                                        >
                                                            {item}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Round context</label>
                                                <textarea
                                                    rows={3}
                                                    placeholder="Briefly explain why you are raising now and the milestones this capital should unlock."
                                                    value={formData.roundContext}
                                                    className={fieldErrors.roundContext ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('roundContext', e.target.value)}
                                                    required
                                                ></textarea>
                                                {fieldErrors.roundContext && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.roundContext}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ---------------- STEP 3: TRACTION ---------------- */}
                                {currentStep === 3 && (
                                    <div className="coa-step-body">
                                        <div className="coa-section-header">
                                            <h2>Current traction</h2>
                                            <p>Add the signals investors should understand</p>
                                        </div>

                                        <div className="coa-grid-2">
                                            <div className="coa-input-group">
                                                <label>Business model</label>
                                                <select
                                                    value={formData.businessModel}
                                                    className={fieldErrors.businessModel ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('businessModel', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select business model</option>
                                                    <option value="B2B SaaS">B2B SaaS</option>
                                                    <option value="Marketplace">Marketplace</option>
                                                    <option value="Usage-based">Usage-based</option>
                                                    <option value="Enterprise License">Enterprise License</option>
                                                </select>
                                                {fieldErrors.businessModel && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.businessModel}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Revenue status</label>
                                                <select
                                                    value={formData.revenueStatus}
                                                    className={fieldErrors.revenueStatus ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('revenueStatus', e.target.value)}
                                                >
                                                    <option value="Revenue generating">Revenue generating</option>
                                                    <option value="Pre-revenue">Pre-revenue</option>
                                                </select>
                                            </div>

                                            <div className="coa-input-group">
                                                <label>Latest monthly revenue</label>
                                                <div className={`coa-input-split ${fieldErrors.monthlyRevenue ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter amount (e.g. 180K or 180,000)"
                                                        value={formData.monthlyRevenue}
                                                        onChange={(e) => handleChange('monthlyRevenue', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                {fieldErrors.monthlyRevenue && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.monthlyRevenue}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Revenue period</label>
                                                <select
                                                    value={formData.revenuePeriod}
                                                    className={fieldErrors.revenuePeriod ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('revenuePeriod', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select month</option>
                                                    <option value="Last Month">Last Month</option>
                                                    <option value="Quarter Average">Quarter Average</option>
                                                    <option value="Annual Run-rate">Annual Run-rate</option>
                                                </select>
                                                {fieldErrors.revenuePeriod && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.revenuePeriod}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group">
                                                <label>12-month revenue growth</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter percentage (e.g. 25% or 150%)"
                                                    value={formData.revenueGrowth}
                                                    className={fieldErrors.revenueGrowth ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('revenueGrowth', e.target.value)}
                                                />
                                                {fieldErrors.revenueGrowth && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.revenueGrowth}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Gross margin</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter percentage (e.g. 75% or 80)"
                                                    value={formData.grossMargin}
                                                    className={fieldErrors.grossMargin ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('grossMargin', e.target.value)}
                                                />
                                                {fieldErrors.grossMargin && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.grossMargin}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group">
                                                <label>Active customers</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter whole number (e.g. 150 or 5k)"
                                                    value={formData.activeCustomers}
                                                    className={fieldErrors.activeCustomers ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('activeCustomers', e.target.value)}
                                                />
                                                {fieldErrors.activeCustomers && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.activeCustomers}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Monthly burn</label>
                                                <div className={`coa-input-split ${fieldErrors.monthlyBurn ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter amount (e.g. 45K or 45,000)"
                                                        value={formData.monthlyBurn}
                                                        onChange={(e) => handleChange('monthlyBurn', e.target.value)}
                                                    />
                                                </div>
                                                {fieldErrors.monthlyBurn && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.monthlyBurn}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group">
                                                <label>Runway remaining</label>
                                                <select
                                                    value={formData.runwayRemaining}
                                                    className={fieldErrors.runwayRemaining ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('runwayRemaining', e.target.value)}
                                                    required
                                                >
                                                    <option value="">Select months</option>
                                                    <option value="< 6 months">&lt; 6 months</option>
                                                    <option value="6 - 12 months">6 – 12 months</option>
                                                    <option value="12 - 18 months">12 – 18 months</option>
                                                    <option value="18+ months">18+ months</option>
                                                </select>
                                                {fieldErrors.runwayRemaining && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.runwayRemaining}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Team size</label>
                                                <input
                                                    type="text"
                                                    placeholder="Full-time team members (e.g. 12)"
                                                    value={formData.teamSize}
                                                    className={fieldErrors.teamSize ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('teamSize', e.target.value)}
                                                />
                                                {fieldErrors.teamSize && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.teamSize}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Key traction signals</label>
                                                <div className="coa-pills-selector">
                                                    {['Recurring revenue', 'Enterprise contracts', 'Strong retention', 'Waitlist', 'Partnerships', 'IP/technology', 'Regulatory approval'].map((signal) => (
                                                        <button
                                                            type="button"
                                                            key={signal}
                                                            className={`coa-tag-pill ${formData.tractionSignals.includes(signal) ? 'selected' : ''}`}
                                                            onClick={() => toggleArrayItem('tractionSignals', signal)}
                                                        >
                                                            {signal}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Your strongest proof point</label>
                                                <textarea
                                                    rows={3}
                                                    placeholder="What single metric, customer result or milestone best demonstrates momentum?"
                                                    value={formData.proofPoint}
                                                    className={fieldErrors.proofPoint ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('proofPoint', e.target.value)}
                                                    required
                                                ></textarea>
                                                {fieldErrors.proofPoint && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.proofPoint}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ---------------- STEP 4: INVESTOR FIT ---------------- */}
                                {currentStep === 4 && (
                                    <div className="coa-step-body">
                                        <div className="coa-section-header">
                                            <h2>Investor preferences</h2>
                                            <p>Help us understand the mandate that fits your round</p>
                                        </div>

                                        <div className="coa-grid-2">
                                            <div className="coa-input-group full-width">
                                                <label>Preferred investor type</label>
                                                <div className="coa-pills-selector">
                                                    {['Angel', 'Venture fund', 'Family office', 'Corporate VC', 'Micro VC', 'Strategic investor'].map((type) => (
                                                        <button
                                                            type="button"
                                                            key={type}
                                                            className={`coa-tag-pill ${formData.investorTypes.includes(type) ? 'selected' : ''}`}
                                                            onClick={() => toggleArrayItem('investorTypes', type)}
                                                        >
                                                            {type}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Target investor geography</label>
                                                <input
                                                    type="text"
                                                    placeholder="Select regions (e.g. India, Singapore, UAE, UK, US)"
                                                    value={formData.targetGeography}
                                                    className={fieldErrors.targetGeography ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('targetGeography', e.target.value)}
                                                    required
                                                />
                                                {fieldErrors.targetGeography && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.targetGeography}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group">
                                                <label>Preferred cheque size (Minimum)</label>
                                                <div className={`coa-input-split ${fieldErrors.minCheque ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Minimum (e.g. 50K or 50,000)"
                                                        value={formData.minCheque}
                                                        onChange={(e) => handleChange('minCheque', e.target.value)}
                                                    />
                                                </div>
                                                {fieldErrors.minCheque && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.minCheque}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Preferred cheque size (Maximum)</label>
                                                <div className={`coa-input-split ${fieldErrors.maxCheque ? 'coa-input-error' : ''}`}>
                                                    <span className="coa-input-prefix">USD</span>
                                                    <input
                                                        type="text"
                                                        placeholder="Maximum (e.g. 500K or 500,000)"
                                                        value={formData.maxCheque}
                                                        onChange={(e) => handleChange('maxCheque', e.target.value)}
                                                    />
                                                </div>
                                                {fieldErrors.maxCheque && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.maxCheque}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Strategic value sought</label>
                                                <div className="coa-pills-selector">
                                                    {['Industry expertise', 'Customer access', 'Hiring network', 'Market expansion', 'Follow-on capital', 'Governance'].map((val) => (
                                                        <button
                                                            type="button"
                                                            key={val}
                                                            className={`coa-tag-pill ${formData.strategicValue.includes(val) ? 'selected' : ''}`}
                                                            onClick={() => toggleArrayItem('strategicValue', val)}
                                                        >
                                                            {val}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="coa-input-group">
                                                <label>Existing investor conversations</label>
                                                <input
                                                    type="text"
                                                    placeholder="None yet / In progress"
                                                    value={formData.existingConversations}
                                                    className={fieldErrors.existingConversations ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('existingConversations', e.target.value)}
                                                />
                                                {fieldErrors.existingConversations && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.existingConversations}</span>
                                                )}
                                            </div>
                                            <div className="coa-input-group">
                                                <label>Investor exclusions or conflicts</label>
                                                <input
                                                    type="text"
                                                    placeholder="List direct competitors, conflicts, or types"
                                                    value={formData.investorExclusions}
                                                    className={fieldErrors.investorExclusions ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('investorExclusions', e.target.value)}
                                                />
                                                {fieldErrors.investorExclusions && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.investorExclusions}</span>
                                                )}
                                            </div>

                                            <div className="coa-input-group full-width">
                                                <label>Your ideal investor</label>
                                                <textarea
                                                    rows={3}
                                                    placeholder="Describe the experience, network or conviction that would make an investor especially valuable."
                                                    value={formData.idealInvestor}
                                                    className={fieldErrors.idealInvestor ? 'coa-input-error' : ''}
                                                    onChange={(e) => handleChange('idealInvestor', e.target.value)}
                                                    required
                                                ></textarea>
                                                {fieldErrors.idealInvestor && (
                                                    <span className="coa-field-error-msg">⚠️ {fieldErrors.idealInvestor}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="coa-checkbox-wrap">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.authAssessment}
                                                    onChange={(e) => handleChange('authAssessment', e.target.checked)}
                                                    required
                                                />
                                                <span>I authorise Markoknow to assess fit against relevant investor mandates. No introduction will be made without confirmation.</span>
                                            </label>
                                            {fieldErrors.authAssessment && (
                                                <span className="coa-field-error-msg" style={{ marginTop: '8px' }}>⚠️ {fieldErrors.authAssessment}</span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ---------------- STEP 5: REVIEW ---------------- */}
                                {currentStep === 5 && (
                                    <div className="coa-step-body">
                                        <div className="coa-section-header">
                                            <h2>Application review</h2>
                                            <p>Confirm that every section reflects your current position</p>
                                        </div>

                                        {/* 2x2 Summary Review Matrix */}
                                        <div className="coa-review-grid">
                                            <div className="coa-review-box">
                                                <div className="coa-rb-head">
                                                    <strong>Company</strong>
                                                    <button type="button" onClick={() => setCurrentStep(1)}>Edit</button>
                                                </div>
                                                <div className="coa-rb-content">
                                                    <h4>{formData.companyName || 'Nova Labs'}</h4>
                                                    <p>{formData.sector || 'B2B SaaS'} · {formData.headquarters || 'Singapore'}</p>
                                                    <span className="coa-rb-link">{formData.website || 'novalabs.com'}</span>
                                                </div>
                                            </div>

                                            <div className="coa-review-box">
                                                <div className="coa-rb-head">
                                                    <strong>Round</strong>
                                                    <button type="button" onClick={() => setCurrentStep(2)}>Edit</button>
                                                </div>
                                                <div className="coa-rb-content">
                                                    <h4>{formData.roundStage || 'Seed'} · USD {formData.targetRaise || '2.5M'} target</h4>
                                                    <p>Target close: {formData.targetCloseDate || 'March 2027'}</p>
                                                </div>
                                            </div>

                                            <div className="coa-review-box">
                                                <div className="coa-rb-head">
                                                    <strong>Traction</strong>
                                                    <button type="button" onClick={() => setCurrentStep(3)}>Edit</button>
                                                </div>
                                                <div className="coa-rb-content">
                                                    <h4>USD {formData.monthlyRevenue || '180K'} MRR</h4>
                                                    <p>{formData.revenueGrowth || '14% MoM growth'} · {formData.runwayRemaining || '18 months runway'}</p>
                                                </div>
                                            </div>

                                            <div className="coa-review-box">
                                                <div className="coa-rb-head">
                                                    <strong>Investor fit</strong>
                                                    <button type="button" onClick={() => setCurrentStep(4)}>Edit</button>
                                                </div>
                                                <div className="coa-rb-content">
                                                    <h4>{formData.investorTypes.slice(0, 3).join(', ')}</h4>
                                                    <p>{formData.targetGeography || 'Singapore · UAE · UK'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Supporting Documents Section */}
                                        <div className="coa-support-materials">
                                            <label>Supporting materials </label>
                                            <div className="coa-materials-row">
                                                {formData.documents && formData.documents.map((doc, idx) => (
                                                    <div key={idx} className="coa-file-chip uploaded">
                                                        <span>{doc.name.toLowerCase().endsWith('.pdf') ? '📄' : doc.name.toLowerCase().endsWith('.xlsx') || doc.name.toLowerCase().endsWith('.xls') || doc.name.toLowerCase().endsWith('.csv') ? '📊' : '📁'}</span>
                                                        {doc.url ? (
                                                            <a href={doc.url} target="_blank" rel="noreferrer" className="coa-file-name" title={doc.name}>
                                                                {doc.name}
                                                            </a>
                                                        ) : (
                                                            <span className="coa-file-name" title={doc.name}>{doc.name}</span>
                                                        )}
                                                        <span className="coa-badge-upload">{doc.isPending ? `${doc.size} · Ready` : doc.size || 'UPLOADED'}</span>
                                                        <button
                                                            type="button"
                                                            className="coa-btn-remove-doc"
                                                            onClick={() => handleRemoveDoc(idx)}
                                                            title="Remove file"
                                                            disabled={loading}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}

                                                {formData.dataRoomLink && (
                                                    <div className="coa-file-chip uploaded">
                                                        <span>🔒</span>
                                                        <a href={formData.dataRoomLink.startsWith('http') ? formData.dataRoomLink : `https://${formData.dataRoomLink}`} target="_blank" rel="noreferrer" className="coa-file-name">
                                                            Data room
                                                        </a>
                                                        <span className="coa-badge-link">LINKED</span>
                                                        <button
                                                            type="button"
                                                            className="coa-btn-remove-doc"
                                                            onClick={() => setFormData((prev) => ({ ...prev, dataRoomLink: '' }))}
                                                            title="Remove link"
                                                            disabled={loading}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                )}

                                                <label className="coa-btn-add-doc" style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
                                                    <input
                                                        type="file"
                                                        multiple
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => handleFileSelection(e, 'Supporting Document')}
                                                        disabled={loading}
                                                        accept=".pdf,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.png,.jpg,.jpeg"
                                                    />
                                                    + Add document
                                                </label>

                                                {!formData.dataRoomLink && !showDataRoomInput && (
                                                    <button
                                                        type="button"
                                                        className="coa-btn-add-doc"
                                                        style={{ borderStyle: 'dotted' }}
                                                        onClick={() => setShowDataRoomInput(true)}
                                                        disabled={loading}
                                                    >
                                                        <span>🔒 + Add Data Room link</span>
                                                    </button>
                                                )}
                                            </div>

                                            {showDataRoomInput && (
                                                <div className="coa-dataroom-input-box">
                                                    <input
                                                        type="text"
                                                        placeholder="Paste data room link (e.g. DocSend, Google Drive, Notion)..."
                                                        value={tempDataRoomLink}
                                                        onChange={(e) => setTempDataRoomLink(e.target.value)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="coa-btn-save-link"
                                                        onClick={() => {
                                                            if (tempDataRoomLink.trim()) {
                                                                setFormData((prev) => ({ ...prev, dataRoomLink: tempDataRoomLink.trim() }));
                                                            }
                                                            setShowDataRoomInput(false);
                                                            setTempDataRoomLink('');
                                                        }}
                                                    >
                                                        Save Link
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '12px' }}
                                                        onClick={() => {
                                                            setShowDataRoomInput(false);
                                                            setTempDataRoomLink('');
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            )}
                                            {fieldErrors.dataRoomLink && (
                                                <span className="coa-field-error-msg" style={{ marginTop: '8px' }}>⚠️ {fieldErrors.dataRoomLink}</span>
                                            )}
                                        </div>

                                        {/* Declarations */}
                                        <div className="coa-declarations">
                                            <label>Declarations</label>
                                            <div className="coa-checkbox-wrap">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.declarationAccuracy}
                                                        onChange={(e) => handleChange('declarationAccuracy', e.target.checked)}
                                                        required
                                                    />
                                                    <span>I confirm that the information in this application is accurate and current.</span>
                                                </label>
                                                {fieldErrors.declarationAccuracy && (
                                                    <span className="coa-field-error-msg" style={{ marginTop: '8px' }}>⚠️ {fieldErrors.declarationAccuracy}</span>
                                                )}
                                            </div>
                                            <div className="coa-checkbox-wrap">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.declarationNoGuarantee}
                                                        onChange={(e) => handleChange('declarationNoGuarantee', e.target.checked)}
                                                        required
                                                    />
                                                    <span>I understand that submission does not guarantee investor introductions, commitments or funding.</span>
                                                </label>
                                                {fieldErrors.declarationNoGuarantee && (
                                                    <span className="coa-field-error-msg" style={{ marginTop: '8px' }}>⚠️ {fieldErrors.declarationNoGuarantee}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Bottom Actions Bar */}
                                <div className="coa-actions-bar">
                                    {currentStep > 1 ? (
                                        <button type="button" className="coa-btn-back" onClick={handleBack} disabled={loading}>
                                            ← Back
                                        </button>
                                    ) : (
                                        <button type="button" className="coa-btn-save-draft">
                                            Save and continue later
                                        </button>
                                    )}

                                    <div className="coa-action-right-group">
                                        {currentStep === 5 && (
                                            <button type="button" className="coa-btn-save-draft" disabled={loading}>
                                                Save draft
                                            </button>
                                        )}
                                        <button type="submit" className="coa-btn-continue" disabled={loading}>
                                            {loading
                                                ? submittingStatus || 'Submitting...'
                                                : currentStep === 1
                                                    ? 'Continue to round details →'
                                                    : currentStep === 2
                                                        ? 'Continue to traction →'
                                                        : currentStep === 3
                                                            ? 'Continue to investor fit →'
                                                            : currentStep === 4
                                                                ? 'Continue to review →'
                                                                : 'Submit for confidential review'}
                                        </button>
                                    </div>
                                </div>

                                <div className="coa-form-bottom-note">
                                    {currentStep === 1 && "Submitting an application does not guarantee investor introductions or funding."}
                                    {currentStep === 2 && "Your figures remain confidential and are used only for initial round assessment."}
                                    {currentStep === 3 && "Use current, verifiable figures. You can provide supporting documents during review."}
                                    {currentStep === 4 && "Preferences guide curation; they do not guarantee a particular investor match."}
                                    {currentStep === 5 && "Your application and documents are handled as confidential business information."}
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Right Dark Context Sidebar */}
                    <aside className="coa-context-sidebar">
                        {/* STEP 1 SIDEBAR */}
                        {currentStep === 1 && (
                            <div className="coa-sidebar-panel">
                                <div className="coa-sp-tag">CONFIDENTIAL REVIEW</div>
                                <h3 className="coa-sp-title">Round readiness</h3>
                                <div className="coa-sp-card">
                                    <div className="coa-sp-card-head">
                                        <strong>Before you begin</strong>
                                        <p>Keep these details ready:</p>
                                    </div>
                                    <ul className="coa-sp-list">
                                        <li>• Current round and target raise</li>
                                        <li>• Revenue, growth and runway</li>
                                        <li>• Existing investors and commitments</li>
                                        <li>• Target investor profile</li>
                                        <li>• Pitch deck and data-room status</li>
                                    </ul>
                                </div>

                                <div className="coa-sp-meta-grid">
                                    <div>
                                        <span className="coa-meta-val">01 / 05</span>
                                        <span className="coa-meta-lbl">APPLICATION STEP</span>
                                    </div>
                                    <div>
                                        <span className="coa-meta-val">10–12 MIN</span>
                                        <span className="coa-meta-lbl">ESTIMATED TIME</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 2 SIDEBAR */}
                        {currentStep === 2 && (
                            <div className="coa-sidebar-panel">
                                <div className="coa-sp-tag">ROUND STRUCTURE</div>
                                <h3 className="coa-sp-title">Sharper parameters, better fit.</h3>
                                <p className="coa-sp-desc">
                                    Investors assess more than the raise amount. A coherent round connects capital, milestones, runway and timing.
                                </p>

                                <ul className="coa-sp-checklist">
                                    <li><span className="coa-ch-dot"></span> Raise target is realistic</li>
                                    <li><span className="coa-ch-dot"></span> Use of funds is specific</li>
                                    <li><span className="coa-ch-dot"></span> Milestones are measurable</li>
                                    <li><span className="coa-ch-dot"></span> Closing timeline is clear</li>
                                </ul>

                                <div className="coa-sp-meta-grid">
                                    <div>
                                        <span className="coa-meta-val">02 / 05</span>
                                        <span className="coa-meta-lbl">APPLICATION STEP</span>
                                    </div>
                                    <div>
                                        <span className="coa-meta-val">40%</span>
                                        <span className="coa-meta-lbl">PROFILE COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 3 SIDEBAR */}
                        {currentStep === 3 && (
                            <div className="coa-sidebar-panel">
                                <div className="coa-sp-tag">TRACTION SIGNALS</div>
                                <h3 className="coa-sp-title">What creates confidence?</h3>
                                <p className="coa-sp-desc">
                                    Strong rounds connect credible evidence to a clear path forward.
                                </p>

                                <ul className="coa-sp-checklist">
                                    <li><span className="coa-ch-dot"></span> Revenue quality and growth</li>
                                    <li><span className="coa-ch-dot"></span> Customer adoption and retention</li>
                                    <li><span className="coa-ch-dot"></span> Healthy margins and runway</li>
                                    <li><span className="coa-ch-dot"></span> Repeatable demand signals</li>
                                </ul>

                                {/* Progress Graph */}
                                <div className="coa-graph-widget">
                                    <svg viewBox="0 0 240 50" className="coa-growth-svg">
                                        <line x1="20" y1="40" x2="120" y2="28" stroke="#5c4eb4" strokeWidth="2" />
                                        <line x1="120" y1="28" x2="220" y2="12" stroke="#5c4eb4" strokeWidth="2" />
                                        <circle cx="20" cy="40" r="4" fill="#5c4eb4" />
                                        <circle cx="120" cy="28" r="4" fill="#5c4eb4" />
                                        <circle cx="220" cy="12" r="4" fill="#5c4eb4" />
                                    </svg>
                                    <div className="coa-gw-labels">
                                        <span>VALIDATE</span>
                                        <span>GROW</span>
                                        <span>SCALE</span>
                                    </div>
                                </div>

                                <div className="coa-sp-meta-grid">
                                    <div>
                                        <span className="coa-meta-val">03 / 05</span>
                                        <span className="coa-meta-lbl">APPLICATION STEP</span>
                                    </div>
                                    <div>
                                        <span className="coa-meta-val">60%</span>
                                        <span className="coa-meta-lbl">PROFILE COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 4 SIDEBAR */}
                        {currentStep === 4 && (
                            <div className="coa-sidebar-panel">
                                <div className="coa-sp-tag">MANDATE FIT</div>
                                <h3 className="coa-sp-title">Fewer, better conversations.</h3>
                                <p className="coa-sp-desc">
                                    Strong matching aligns both sides before an introduction is considered.
                                </p>

                                {/* Alignment Score Matrix */}
                                <div className="coa-matrix-widget">
                                    <div className="coa-mw-row">
                                        <span>Stage</span>
                                        <span className="coa-mw-pill green">ALIGNED</span>
                                    </div>
                                    <div className="coa-mw-row">
                                        <span>Sector</span>
                                        <span className="coa-mw-pill purple">STRONG</span>
                                    </div>
                                    <div className="coa-mw-row">
                                        <span>Cheque</span>
                                        <span className="coa-mw-pill green">ALIGNED</span>
                                    </div>
                                    <div className="coa-mw-row">
                                        <span>Geography</span>
                                        <span className="coa-mw-pill gray">OPEN</span>
                                    </div>
                                    <div className="coa-mw-row">
                                        <span>Strategic fit</span>
                                        <span className="coa-mw-pill purple">HIGH</span>
                                    </div>
                                </div>

                                <div className="coa-sp-meta-grid">
                                    <div>
                                        <span className="coa-meta-val">04 / 05</span>
                                        <span className="coa-meta-lbl">APPLICATION STEP</span>
                                    </div>
                                    <div>
                                        <span className="coa-meta-val">80%</span>
                                        <span className="coa-meta-lbl">PROFILE COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 5 SIDEBAR */}
                        {currentStep === 5 && (
                            <div className="coa-sidebar-panel">
                                <div className="coa-sp-tag">FINAL CHECK</div>
                                <h3 className="coa-sp-title">Your round, in context.</h3>
                                <p className="coa-sp-desc">
                                    The Capital Office team will assess readiness, mandate relevance and any preparation gaps.
                                </p>

                                <div className="coa-review-steps-widget">
                                    <div className="coa-rsw-item">
                                        <div className="coa-rsw-num">01</div>
                                        <div>
                                            <strong>Initial assessment</strong>
                                            <p>Round structure and evidence</p>
                                        </div>
                                    </div>
                                    <div className="coa-rsw-item">
                                        <div className="coa-rsw-num">02</div>
                                        <div>
                                            <strong>Readiness review</strong>
                                            <p>Materials and investor narrative</p>
                                        </div>
                                    </div>
                                    <div className="coa-rsw-item">
                                        <div className="coa-rsw-num">03</div>
                                        <div>
                                            <strong>Fit decision</strong>
                                            <p>Relevant mandate or preparation path</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="coa-review-time-pill">
                                    REVIEW RESPONSE WITHIN 5–7 BUSINESS DAYS
                                </div>

                                <div className="coa-sp-meta-grid">
                                    <div>
                                        <span className="coa-meta-val">05 / 05</span>
                                        <span className="coa-meta-lbl">APPLICATION STEP</span>
                                    </div>
                                    <div>
                                        <span className="coa-meta-val">100%</span>
                                        <span className="coa-meta-lbl">PROFILE COMPLETE</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="coa-sidebar-footer-note">
                            {currentStep === 1 && "Your information is securely processed and reviewed solely for mandate qualification."}
                            {currentStep === 2 && "Markoknow does not guarantee investor introductions, commitments or funding."}
                            {currentStep === 3 && "Traction is assessed in the context of your stage, sector and business model."}
                            {currentStep === 4 && "Investor details and active mandates remain confidential until mutual relevance is established."}
                            {currentStep === 5 && "You can update your application if material information changes before review."}
                        </div>
                    </aside>
                </div>
            </div>
        );
    };

    export default CapitalOfficeApplication;